import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, Star, Package } from 'lucide-react';
import { clsx } from 'clsx';
import { useProduct, useUpdateProduct, useUpdateVendorProduct } from '../../hooks/useProducts';
import PageHeader from '../../components/common/PageHeader';
import type { VendorProduct } from '../../api/products';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading, error } = useProduct(id!);
  const updateProduct = useUpdateProduct();
  const updateVendorProduct = useUpdateVendorProduct();
  const [editingVendor, setEditingVendor] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
  } = useForm();

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-20">
        <p className="text-red-500 dark:text-red-400 mb-2">Failed to load product</p>
        <button onClick={() => navigate('/products')} className="btn-secondary">Back to Products</button>
      </div>
    );
  }

  const onSaveProduct = (values: Record<string, unknown>) => {
    updateProduct.mutate({ id: id!, data: values });
  };

  const onSaveVendorProduct = (vp: VendorProduct, values: Record<string, unknown>) => {
    updateVendorProduct.mutate(
      { upc: product.upc, sku: vp.sku, data: values },
      { onSuccess: () => setEditingVendor(null) },
    );
  };

  return (
    <div>
      <PageHeader
        title={product.productName}
        description={`UPC: ${product.upc}`}
        actions={
          <button onClick={() => navigate('/products')} className="btn-secondary gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        }
      />

      <div className="max-w-4xl space-y-4 lg:space-y-6">
        {/* Product Info */}
        <form onSubmit={handleSubmit(onSaveProduct)} className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Product Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-4 lg:mb-6">
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Service Type</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{product.serviceType}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Process Type</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{product.processType}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product Type</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{product.productType}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Delivery Method</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{product.methodOfDelivery}</dd>
            </div>
          </dl>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
            <div>
              <label className="label">Description</label>
              <input
                type="text"
                className="input"
                defaultValue={product.description}
                {...register('description')}
              />
            </div>
            <div>
              <label className="label">Price</label>
              <input
                type="number"
                step="0.01"
                className="input"
                defaultValue={product.price}
                {...register('price', { valueAsNumber: true })}
              />
            </div>
            <div>
              <label className="label">Max ETA (days)</label>
              <input
                type="number"
                className="input"
                defaultValue={product.maxEtaDays}
                {...register('maxEtaDays', { valueAsNumber: true })}
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button type="submit" disabled={updateProduct.isPending} className="btn-primary gap-1">
              <Save className="h-4 w-4" />
              Save Changes
            </button>
          </div>
        </form>

        {/* Vendor Products */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">
            Vendor Products
            <span className="text-sm font-normal text-gray-400 dark:text-gray-500 ml-2">
              ({product.vendorProducts?.length || 0})
            </span>
          </h2>

          {(!product.vendorProducts || product.vendorProducts.length === 0) ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No vendor products configured.</p>
          ) : (
            <div className="space-y-3">
              {product.vendorProducts.map((vp) => {
                const isFirstClose = vp.vendorKey === 'first-close-one';
                const isEditing = editingVendor === vp.sku;

                return (
                  <div
                    key={vp.sku}
                    className={clsx(
                      'rounded-lg border p-3 lg:p-4',
                      isFirstClose
                        ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700',
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Package className={clsx('h-5 w-5', isFirstClose ? 'text-emerald-600' : 'text-gray-400')} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {vp.vendorName || vp.vendorKey}
                            </span>
                            {isFirstClose && (
                              <span className="badge bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                FirstClose One
                              </span>
                            )}
                            {vp.isPreferred && (
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            SKU: {vp.sku} &middot; ${vp.price?.toFixed(2)} &middot; ETA: {vp.maxEtaDays}d
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setEditingVendor(isEditing ? null : vp.sku)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>

                    {isEditing && (
                      <VendorProductEditForm
                        vendorProduct={vp}
                        onSave={(values) => onSaveVendorProduct(vp, values)}
                        isPending={updateVendorProduct.isPending}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VendorProductEditForm({
  vendorProduct,
  onSave,
  isPending,
}: {
  vendorProduct: VendorProduct;
  onSave: (values: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  const { register, handleSubmit } = useForm({
    defaultValues: {
      price: vendorProduct.price,
      maxEtaDays: vendorProduct.maxEtaDays,
      isPreferred: vendorProduct.isPreferred,
      isOrderCancelEnabled: vendorProduct.isOrderCancelEnabled,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSave)} className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="label">Price</label>
          <input type="number" step="0.01" className="input" {...register('price', { valueAsNumber: true })} />
        </div>
        <div>
          <label className="label">Max ETA (days)</label>
          <input type="number" className="input" {...register('maxEtaDays', { valueAsNumber: true })} />
        </div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm dark:text-gray-200">
            <input type="checkbox" className="rounded" {...register('isPreferred')} />
            Preferred
          </label>
          <label className="flex items-center gap-2 text-sm dark:text-gray-200">
            <input type="checkbox" className="rounded" {...register('isOrderCancelEnabled')} />
            Cancel enabled
          </label>
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={isPending} className="btn-primary text-xs w-full">
            <Save className="h-3 w-3 mr-1" />
            Save
          </button>
        </div>
      </div>
    </form>
  );
}
